import { lazy, useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import type { LabelsType } from '@/components/signup/bxpkit-signup/src/model/utils';
import TryIntent from '@/components/try-intent/src/try-intent';
import { useProductIntent } from '@/components/user-context/helpers/use-product-intent';
import {
	defaultFeatureFlags,
	defaultLabels,
	defaultProductData,
	firstImpressionsImageUrls,
} from '@/templates/try/cloud/signup/signup-page/src/utils/data-mappings';
import {
	FIRST_IMPRESSIONS_LAYOUT,
	getQueryParams,
} from '@/templates/try/cloud/signup/signup-page/src/utils/query-param-resolver';
import { useLabelsLocalized } from '@/templates/try/cloud/signup/signup-page/src/utils/use-labels-localized';
import { ClientOnly } from '@/utils/client-only';
import { ExperimentView } from '@/utils/experiment/experiment-view';
import { experiments } from '@/utils/experiment/manifest';
import { useLocation } from '@/utils/location-provider';

import { getBitbucketSignupPrValueTreatmentLabels } from './bitbucket-signup-pr-value-labels';

const SignUpLayoutContainer = lazy(
	() =>
		// @ts-ignore
		import('../../../../../../../components/signup/bxpkit-signup/src/sign-up-layout-container.tsx'),
);

export const SignupDefault = () => {
	const location = useLocation();
	const labels = useLabelsLocalized(defaultLabels);
	const { layout } = getQueryParams(location?.search ?? '');
	const { bundle, edition } = useProductIntent(location.href);
	const intl = useIntl();

	const treatmentLabels = useMemo(
		() => getBitbucketSignupPrValueTreatmentLabels(labels, intl),
		[intl, labels],
	);

	const renderSignup = useCallback(
		(signupLabels: LabelsType) => (
			<SignUpLayoutContainer
				layout={layout}
				allowSignupWithGoogle={true}
				allowSignupWithAllSocialOptions={true}
				bundleOverride={bundle}
				editionOverride={edition}
				productData={defaultProductData}
				enableFreeBreachTest={true}
				labels={signupLabels}
				firstImpressionsImageUrls={firstImpressionsImageUrls}
				featureFlags={defaultFeatureFlags}
			/>
		),
		[bundle, edition, layout],
	);

	const renderExperiment = useCallback(
		(isEnabled: boolean) => renderSignup(isEnabled ? treatmentLabels : labels),
		[labels, renderSignup, treatmentLabels],
	);

	const isFirstImpressionsLayout = layout === FIRST_IMPRESSIONS_LAYOUT;

	return (
		<ClientOnly>
			<TryIntent intentType="form" productKey={bundle} edition={edition}>
				{isFirstImpressionsLayout ? (
					<ExperimentView
						experiment={experiments.twcg2271BitbucketSignupPrValue}
						render={renderExperiment}
					/>
				) : (
					renderSignup(labels)
				)}
			</TryIntent>
		</ClientOnly>
	);
};
